<template>
  <div class="kpi-admin">

    <v-tabs v-model="activeTab" :color="APP_Color" class="mb-4">
      <v-tab value="definitii">
        <v-icon size="18" class="mr-1">mdi-cog</v-icon>
        Definiții KPI
      </v-tab>
      <v-tab value="agenti">
        <v-icon size="18" class="mr-1">mdi-account-group</v-icon>
        Roluri Agenți
      </v-tab>
      <v-tab value="targete">
        <v-icon size="18" class="mr-1">mdi-target-variant</v-icon>
        Targete Individuale
      </v-tab>
      <v-tab value="echipa">
        <v-icon size="18" class="mr-1">mdi-chart-bar</v-icon>
        Raport Echipă
      </v-tab>
    </v-tabs>

    <v-window v-model="activeTab">

      <!-- ─── Tab: Definiții KPI ─────────────────────────────────────── -->
      <v-window-item value="definitii">
        <v-card elevation="2" class="admin-card">
          <v-card-title class="card-title-row">
            <span>Definiții KPI</span>
            <v-btn :color="APP_Color" size="small" @click="openDefinitieDialog()">
              <v-icon size="16" class="mr-1">mdi-plus</v-icon> Adaugă
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-table class="admin-table">
              <thead>
                <tr>
                  <th>Cod</th>
                  <th>Denumire</th>
                  <th>Tip Agent</th>
                  <th>Target Lunar</th>
                  <th>Tip Calcul</th>
                  <th>Activ</th>
                  <th>Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loadingDefinitii">
                  <td colspan="7" class="text-center py-4">
                    <v-progress-circular indeterminate :color="APP_Color" size="24" />
                  </td>
                </tr>
                <tr v-else v-for="def in definitii" :key="def.ID">
                  <td><code>{{ def.Cod }}</code></td>
                  <td>{{ def.Denumire }}</td>
                  <td>
                    <v-chip :color="def.TipAgent === 'Hunter' ? 'deep-orange' : def.TipAgent === 'Farmer' ? 'green' : 'blue'"
                      size="x-small">{{ def.TipAgent }}</v-chip>
                  </td>
                  <td>{{ def.TargetLunarDefault }}</td>
                  <td>
                    <v-chip :color="def.TipCalcul === 'Automat' ? 'blue' : 'grey'" size="x-small" variant="tonal">
                      {{ def.TipCalcul }}
                    </v-chip>
                  </td>
                  <td>
                    <v-icon :color="def.IsActiv ? 'green' : 'grey'" size="18">
                      {{ def.IsActiv ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </v-icon>
                  </td>
                  <td>
                    <v-btn icon size="x-small" variant="text" @click="openDefinitieDialog(def)">
                      <v-icon size="16">mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn icon size="x-small" variant="text" color="error" @click="deleteDefinitie(def)">
                      <v-icon size="16">mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- ─── Tab: Roluri Agenți ─────────────────────────────────────── -->
      <v-window-item value="agenti">
        <v-card elevation="2" class="admin-card">
          <v-card-title class="card-title-row">
            <span>Roluri Agenți (Hunter / Farmer)</span>
            <v-btn :color="APP_Color" size="small" @click="openAgentDialog()">
              <v-icon size="16" class="mr-1">mdi-plus</v-icon> Adaugă
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-table class="admin-table">
              <thead>
                <tr>
                  <th>Utilizator</th>
                  <th>Tip Agent</th>
                  <th>Data Start</th>
                  <th>Data End</th>
                  <th>Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loadingAgenti">
                  <td colspan="5" class="text-center py-4">
                    <v-progress-circular indeterminate :color="APP_Color" size="24" />
                  </td>
                </tr>
                <tr v-else v-for="agent in agenti" :key="agent.ID">
                  <td>{{ agent.Utilizator }}</td>
                  <td>
                    <v-chip :color="agent.TipAgent === 'Hunter' ? 'deep-orange' : 'green'" size="x-small">
                      {{ agent.TipAgent }}
                    </v-chip>
                  </td>
                  <td>{{ formatDate(agent.DataStart) }}</td>
                  <td>{{ formatDate(agent.DataEnd) || '—' }}</td>
                  <td>
                    <v-btn icon size="x-small" variant="text" @click="openAgentDialog(agent)">
                      <v-icon size="16">mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn icon size="x-small" variant="text" color="error" @click="deleteAgent(agent)">
                      <v-icon size="16">mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- ─── Tab: Targete Individuale ──────────────────────────────── -->
      <v-window-item value="targete">
        <v-card elevation="2" class="admin-card">
          <v-card-title class="card-title-row">
            <div class="d-flex align-center gap-2">
              <span>Targete Individuale</span>
              <v-select v-model="targetAn" :items="ani" density="compact" variant="outlined" hide-details
                class="filter-select ml-3" @update:modelValue="incarcaTargete" />
              <v-select v-model="targetLuna" :items="luni" item-title="label" item-value="value" density="compact"
                variant="outlined" hide-details class="filter-select ml-2" @update:modelValue="incarcaTargete" />
            </div>
            <v-btn :color="APP_Color" size="small" @click="openTargetDialog()">
              <v-icon size="16" class="mr-1">mdi-plus</v-icon> Adaugă Override
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-table class="admin-table">
              <thead>
                <tr>
                  <th>Utilizator</th>
                  <th>KPI</th>
                  <th>An</th>
                  <th>Lună</th>
                  <th>Target Override</th>
                  <th>Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loadingTargete">
                  <td colspan="6" class="text-center py-4">
                    <v-progress-circular indeterminate :color="APP_Color" size="24" />
                  </td>
                </tr>
                <tr v-else v-for="t in targete" :key="t.ID">
                  <td>{{ t.Utilizator }}</td>
                  <td>{{ t.KPIDefinitie?.Denumire || t.KPIDefinitieID }}</td>
                  <td>{{ t.An }}</td>
                  <td>{{ numeleLunii(t.Luna) }}</td>
                  <td><strong>{{ t.Target }}</strong></td>
                  <td>
                    <v-btn icon size="x-small" variant="text" @click="openTargetDialog(t)">
                      <v-icon size="16">mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn icon size="x-small" variant="text" color="error" @click="deleteTarget(t)">
                      <v-icon size="16">mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- ─── Tab: Raport Echipă ─────────────────────────────────────── -->
      <v-window-item value="echipa">
        <v-card elevation="2" class="admin-card">
          <v-card-title class="card-title-row">
            <div class="d-flex align-center gap-2">
              <span>Raport KPI Echipă</span>
              <v-select v-model="echipaAn" :items="ani" density="compact" variant="outlined" hide-details
                class="filter-select ml-3" />
              <v-select v-model="echipaLuna" :items="luni" item-title="label" item-value="value" density="compact"
                variant="outlined" hide-details class="filter-select ml-2" />
            </div>
            <v-btn :color="APP_Color" size="small" :loading="loadingEchipa" @click="incarcaEchipa">
              <v-icon size="16" class="mr-1">mdi-refresh</v-icon> Încarcă
            </v-btn>
          </v-card-title>

          <v-card-text>
            <div v-if="loadingEchipa" class="d-flex justify-center py-6">
              <v-progress-circular indeterminate :color="APP_Color" size="40" />
            </div>
            <div v-else-if="echipaData.length === 0" class="text-center text-grey py-4">
              Apăsați „Încarcă" pentru a vedea raportul echipei.
            </div>
            <div v-else>
              <v-expansion-panels>
                <v-expansion-panel v-for="member in echipaData" :key="member.Utilizator">
                  <v-expansion-panel-title>
                    <div class="d-flex align-center gap-2">
                      <v-icon size="18">mdi-account</v-icon>
                      <strong>{{ member.Utilizator }}</strong>
                      <v-chip :color="member.TipAgent === 'Hunter' ? 'deep-orange' : 'green'" size="x-small">
                        {{ member.TipAgent }}
                      </v-chip>
                      <v-chip size="x-small" :color="medieMemberColor(member.KPIs)" variant="tonal">
                        {{ medieMember(member.KPIs) }}% medie
                      </v-chip>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <v-table density="compact">
                      <thead>
                        <tr>
                          <th>KPI</th>
                          <th>Target</th>
                          <th>Realizat</th>
                          <th>%</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="kpi in member.KPIs" :key="kpi.KPIDefinitieID">
                          <td>{{ kpi.Denumire }}</td>
                          <td>{{ kpi.Target }}</td>
                          <td :style="{ color: getKPIColor(kpi.ProcentRealizat), fontWeight: '700' }">
                            {{ kpi.Realizat }}
                          </td>
                          <td>
                            <v-chip :color="kpiChipColor(kpi.ProcentRealizat)" size="x-small" label>
                              {{ kpi.ProcentRealizat }}%
                            </v-chip>
                          </td>
                        </tr>
                      </tbody>
                    </v-table>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </v-card-text>
        </v-card>
      </v-window-item>

    </v-window>

    <!-- ─── Dialog Definiție KPI ──────────────────────────────────────── -->
    <v-dialog v-model="definitieDialog" max-width="560">
      <v-card>
        <v-card-title>{{ definitieForm.ID ? 'Editare' : 'Adăugare' }} Definiție KPI</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field v-model="definitieForm.Cod" label="Cod (unic)" density="compact" variant="outlined" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select v-model="definitieForm.TipAgent" :items="['Hunter', 'Farmer', 'Ambele']" label="Tip Agent"
                density="compact" variant="outlined" />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="definitieForm.Denumire" label="Denumire" density="compact" variant="outlined" />
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="definitieForm.Descriere" label="Descriere" rows="2" density="compact"
                variant="outlined" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model.number="definitieForm.TargetLunarDefault" label="Target Lunar Default" type="number"
                density="compact" variant="outlined" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select v-model="definitieForm.TipCalcul" :items="['Automat', 'Manual']" label="Tip Calcul"
                density="compact" variant="outlined" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="definitieForm.SursaDate" label="Sursă Date (cod intern)" density="compact"
                variant="outlined" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model.number="definitieForm.Ordine" label="Ordine afișare" type="number" density="compact"
                variant="outlined" />
            </v-col>
            <v-col cols="12">
              <v-switch v-model="definitieForm.IsActiv" label="Activ" density="compact" :color="APP_Color" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="definitieDialog = false">Anulare</v-btn>
          <v-btn :color="APP_Color" :loading="savingDefinitie" @click="saveDefinitie">Salvare</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ─── Dialog Rol Agent ──────────────────────────────────────────── -->
    <v-dialog v-model="agentDialog" max-width="480">
      <v-card>
        <v-card-title>{{ agentForm.ID ? 'Editare' : 'Adăugare' }} Rol Agent</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12">
              <v-text-field v-model="agentForm.Utilizator" label="Username" density="compact" variant="outlined" />
            </v-col>
            <v-col cols="12">
              <v-select v-model="agentForm.TipAgent" :items="['Hunter', 'Farmer']" label="Tip Agent" density="compact"
                variant="outlined" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="agentForm.DataStart" label="Data Start" type="date" density="compact"
                variant="outlined" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="agentForm.DataEnd" label="Data End (opțional)" type="date" density="compact"
                variant="outlined" clearable />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="agentDialog = false">Anulare</v-btn>
          <v-btn :color="APP_Color" :loading="savingAgent" @click="saveAgent">Salvare</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ─── Dialog Target Individ ────────────────────────────────────── -->
    <v-dialog v-model="targetDialog" max-width="480">
      <v-card>
        <v-card-title>{{ targetForm.ID ? 'Editare' : 'Adăugare' }} Target</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12">
              <v-text-field v-model="targetForm.Utilizator" label="Username" density="compact" variant="outlined" />
            </v-col>
            <v-col cols="12">
              <v-select v-model="targetForm.KPIDefinitieID" :items="definitii" item-title="Denumire" item-value="ID"
                label="KPI" density="compact" variant="outlined" />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field v-model.number="targetForm.An" label="An" type="number" density="compact"
                variant="outlined" />
            </v-col>
            <v-col cols="12" sm="4">
              <v-select v-model="targetForm.Luna" :items="luni" item-title="label" item-value="value" label="Lună"
                density="compact" variant="outlined" />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field v-model.number="targetForm.Target" label="Target Override" type="number" density="compact"
                variant="outlined" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="targetDialog = false">Anulare</v-btn>
          <v-btn :color="APP_Color" :loading="savingTarget" @click="saveTarget">Salvare</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import AppSettings from '@/settings/appSettings'
import CrmKPIService from '@/services/Comercial/CrmKPI'
import { confirm } from 'devextreme/ui/dialog'
import notify from 'devextreme/ui/notify'

const emptyDefinitie = () => ({
  ID: null as string | null,
  Cod: '',
  Denumire: '',
  Descriere: '',
  TipAgent: 'Hunter',
  TargetLunarDefault: 0,
  TipCalcul: 'Automat',
  SursaDate: '',
  Ordine: 10,
  IsActiv: true,
})

const emptyAgent = () => ({
  ID: null as string | null,
  Utilizator: '',
  TipAgent: 'Hunter',
  DataStart: new Date().toISOString().slice(0, 10),
  DataEnd: '',
})

const emptyTarget = () => ({
  ID: null as string | null,
  Utilizator: '',
  KPIDefinitieID: null as string | null,
  An: new Date().getFullYear(),
  Luna: new Date().getMonth() + 1,
  Target: 0,
})

export default defineComponent({
  name: 'KPIAdmin',

  data() {
    return {
      APP_Color: AppSettings.APP_Color,
      activeTab: 'definitii',

      // ── Definiții ──
      definitii: [] as any[],
      loadingDefinitii: false,
      definitieDialog: false,
      savingDefinitie: false,
      definitieForm: emptyDefinitie(),

      // ── Agenți ──
      agenti: [] as any[],
      loadingAgenti: false,
      agentDialog: false,
      savingAgent: false,
      agentForm: emptyAgent(),

      // ── Targete ──
      targete: [] as any[],
      loadingTargete: false,
      targetDialog: false,
      savingTarget: false,
      targetForm: emptyTarget(),
      targetAn: new Date().getFullYear(),
      targetLuna: new Date().getMonth() + 1,

      // ── Echipă ──
      echipaData: [] as any[],
      loadingEchipa: false,
      echipaAn: new Date().getFullYear(),
      echipaLuna: new Date().getMonth() + 1,

      ani: Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i),
      luni: [
        { label: 'Ianuarie', value: 1 },
        { label: 'Februarie', value: 2 },
        { label: 'Martie', value: 3 },
        { label: 'Aprilie', value: 4 },
        { label: 'Mai', value: 5 },
        { label: 'Iunie', value: 6 },
        { label: 'Iulie', value: 7 },
        { label: 'August', value: 8 },
        { label: 'Septembrie', value: 9 },
        { label: 'Octombrie', value: 10 },
        { label: 'Noiembrie', value: 11 },
        { label: 'Decembrie', value: 12 },
      ],
    }
  },

  mounted() {
    this.incarcaDefinitii()
    this.incarcaAgenti()
    this.incarcaTargete()
  },

  methods: {
    // ── Definiții ──────────────────────────────────────────────────────────

    async incarcaDefinitii() {
      this.loadingDefinitii = true
      try {
        const res = await CrmKPIService.getDefinitii({ '$orderby': 'Ordine asc' })
        this.definitii = res.data?.value || []
      } catch (e) { console.error(e) } finally { this.loadingDefinitii = false }
    },

    openDefinitieDialog(item?: any) {
      this.definitieForm = item
        ? { ...item }
        : emptyDefinitie()
      this.definitieDialog = true
    },

    async saveDefinitie() {
      this.savingDefinitie = true
      try {
        if (this.definitieForm.ID) {
          const { ID, UserCreat, DataCreat, UserModificat, DataModificat, KPIDefinitie, ...payload } = this.definitieForm as any
          await CrmKPIService.updateDefinitie(this.definitieForm.ID!, payload)
        } else {
          const { ID, ...payload } = this.definitieForm as any
          await CrmKPIService.saveDefinitie(payload)
        }
        this.definitieDialog = false
        await this.incarcaDefinitii()
      } catch (e) { console.error(e) } finally { this.savingDefinitie = false }
    },

    async deleteDefinitie(item: any) {
      const result = await confirm(`Ștergeți definiția "${item.Denumire}"?`, "Confirmare Ștergere")
      if (!result) return
      try {
        await CrmKPIService.deleteDefinitie(item.ID)
        await this.incarcaDefinitii()
        notify('Definiție ștearsă!', 'success', 2000)
      } catch (e) {
        console.error(e)
        notify('Eroare la ștergere!', 'error', 2000)
      }
    },

    // ── Agenți ────────────────────────────────────────────────────────────

    async incarcaAgenti() {
      this.loadingAgenti = true
      try {
        const res = await CrmKPIService.getAgenti({ '$orderby': 'Utilizator asc' })
        this.agenti = res.data?.value || []
      } catch (e) { console.error(e) } finally { this.loadingAgenti = false }
    },

    openAgentDialog(item?: any) {
      this.agentForm = item
        ? { ...item, DataStart: item.DataStart?.slice(0, 10) || '', DataEnd: item.DataEnd?.slice(0, 10) || '' }
        : emptyAgent()
      this.agentDialog = true
    },

    async saveAgent() {
      this.savingAgent = true
      try {
        const payload: any = {
          Utilizator: this.agentForm.Utilizator,
          TipAgent: this.agentForm.TipAgent,
          DataStart: this.agentForm.DataStart || null,
          DataEnd: this.agentForm.DataEnd || null,
        }
        if (this.agentForm.ID) {
          await CrmKPIService.updateAgent(this.agentForm.ID!, payload)
        } else {
          await CrmKPIService.saveAgent(payload)
        }
        this.agentDialog = false
        await this.incarcaAgenti()
      } catch (e) { console.error(e) } finally { this.savingAgent = false }
    },

    async deleteAgent(item: any) {
      const result = await confirm(`Ștergeți rolul pentru "${item.Utilizator}"?`, "Confirmare Ștergere")
      if (!result) return
      try {
        await CrmKPIService.deleteAgent(item.ID)
        await this.incarcaAgenti()
        notify('Rol șters!', 'success', 2000)
      } catch (e) {
        console.error(e)
        notify('Eroare la ștergere!', 'error', 2000)
      }
    },

    // ── Targete ───────────────────────────────────────────────────────────

    async incarcaTargete() {
      this.loadingTargete = true
      try {
        const res = await CrmKPIService.getTargeturiUser({
          '$filter': `An eq ${this.targetAn} and Luna eq ${this.targetLuna}`,
          '$expand': 'KPIDefinitie($select=Denumire)',
          '$orderby': 'Utilizator asc'
        })
        this.targete = res.data?.value || []
      } catch (e) { console.error(e) } finally { this.loadingTargete = false }
    },

    openTargetDialog(item?: any) {
      this.targetForm = item
        ? { ...item }
        : { ...emptyTarget(), An: this.targetAn, Luna: this.targetLuna }
      this.targetDialog = true
    },

    async saveTarget() {
      this.savingTarget = true
      try {
        const payload: any = {
          KPIDefinitieID: this.targetForm.KPIDefinitieID,
          Utilizator: this.targetForm.Utilizator,
          An: this.targetForm.An,
          Luna: this.targetForm.Luna,
          Target: this.targetForm.Target,
        }
        if (this.targetForm.ID) {
          await CrmKPIService.updateTargetUser(this.targetForm.ID!, payload)
        } else {
          await CrmKPIService.saveTargetUser(payload)
        }
        this.targetDialog = false
        await this.incarcaTargete()
      } catch (e) { console.error(e) } finally { this.savingTarget = false }
    },

    async deleteTarget(item: any) {
      const result = await confirm('Ștergeți override-ul de target?', "Confirmare Ștergere")
      if (!result) return
      try {
        await CrmKPIService.deleteTargetUser(item.ID)
        await this.incarcaTargete()
        notify('Override șters!', 'success', 2000)
      } catch (e) {
        console.error(e)
        notify('Eroare la ștergere!', 'error', 2000)
      }
    },

    // ── Echipă ────────────────────────────────────────────────────────────

    async incarcaEchipa() {
      this.loadingEchipa = true
      try {
        const res = await CrmKPIService.getEchipaRaport(this.echipaAn, this.echipaLuna)
        this.echipaData = res.data || []
      } catch (e) { console.error(e) } finally { this.loadingEchipa = false }
    },

    // ── Helpers ───────────────────────────────────────────────────────────

    formatDate(val: string | null | undefined): string {
      if (!val) return ''
      return new Date(val).toLocaleDateString('ro-RO')
    },

    numeleLunii(luna: number): string {
      const map: Record<number, string> = {
        1: 'Ian', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'Mai', 6: 'Iun',
        7: 'Iul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec'
      }
      return map[luna] || luna.toString()
    },

    getKPIColor(procent: number): string {
      if (procent >= 100) return '#4CAF50'
      if (procent >= 70) return '#FF9800'
      return '#F44336'
    },

    kpiChipColor(procent: number): string {
      if (procent >= 100) return 'success'
      if (procent >= 70) return 'warning'
      return 'error'
    },

    medieMember(kpis: any[]): number {
      if (!kpis?.length) return 0
      return Math.round(kpis.reduce((a, k) => a + (k.ProcentRealizat || 0), 0) / kpis.length)
    },

    medieMemberColor(kpis: any[]): string {
      return this.kpiChipColor(this.medieMember(kpis))
    },
  }
})
</script>

<style scoped>
.kpi-admin {
  padding: 2rem;
  background: linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%);
  min-height: 100vh;
}

.admin-card {
  border-radius: 16px !important;
}

.card-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 600;
  font-size: 1.1rem;
}

.admin-table th {
  font-size: 0.75rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  color: #718096 !important;
  font-weight: 600 !important;
  padding: 0.75rem 1.25rem !important;
  border-bottom: 2px solid #edf2f7 !important;
}

.admin-table td {
  padding: 0.75rem 1.25rem !important;
  font-size: 0.875rem !important;
  border-bottom: 1px solid #edf2f7 !important;
}

.filter-select {
  max-width: 140px;
}

.gap-2 {
  gap: 0.5rem;
}

@media (max-width: 600px) {
  .kpi-admin {
    padding: 1rem;
  }
}
</style>
